import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1721577590783-049fa2d742ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxtZW5zJTIwc3RyZWV0d2VhciUyMGZhc2hpb24lMjBwb3J0cmFpdCUyMG1vZGVsJTIwYmxhY2slMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4OTg4NzgwNnww&ixlib=rb-4.1.0&q=85";
const FLOAT_1 =
  "https://images.unsplash.com/photo-1564557287817-3785e38ec1f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxvdmVyc2l6ZWQlMjBob29kaWUlMjBzdHJlZXR3ZWFyJTIwZmFzaGlvbiUyMG1lbnxlbnwwfHx8fDE3ODk4ODc4MTR8MA&ixlib=rb-4.1.0&q=85";
const FLOAT_2 =
  "https://images.unsplash.com/photo-1521223890158-f9f7c3d5d504?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxsZWF0aGVyJTIwamFja2V0JTIwc3RyZWV0d2VhciUyMGZhc2hpb24lMjBtYWxlfGVufDB8fHx8MTc4OTg4NzgxNHww&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();

  const onMove = (e) => {
    if (reduce || !ref.current) return;
    const { clientX, clientY } = e;
    const x = (clientX / window.innerWidth - 0.5) * 2;
    const y = (clientY / window.innerHeight - 0.5) * 2;
    ref.current.style.setProperty("--px", x);
    ref.current.style.setProperty("--py", y);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      className="relative min-h-screen overflow-hidden grain light-field flex items-center"
      data-testid="hero-section"
    >
      {/* Parallax background image */}
      <div
        className="absolute inset-0 opacity-40"
        style={{
          transform:
            "translate3d(calc(var(--px, 0) * -18px), calc(var(--py, 0) * -18px), 0) scale(1.1)",
          transition: "transform 0.3s ease-out",
        }}
      >
        <img
          src={HERO_IMG}
          alt="THRIFTHOUSE streetwear model"
          className="w-full h-full object-cover object-top"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0C0E] via-[#0B0C0E]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-transparent to-[#0B0C0E]/60" />
      </div>

      {/* Floating garments */}
      <div
        className="hidden lg:block absolute right-[8%] top-[18%] w-48 h-64 rounded-sm overflow-hidden border border-white/10 shadow-2xl float-slow"
        style={{
          transform:
            "translate3d(calc(var(--px, 0) * 30px), calc(var(--py, 0) * 30px), 0)",
        }}
      >
        <img src={FLOAT_1} alt="Floating hoodie" className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div
        className="hidden lg:block absolute right-[26%] bottom-[14%] w-40 h-56 rounded-sm overflow-hidden border border-white/10 shadow-2xl float-med"
        style={{
          transform:
            "translate3d(calc(var(--px, 0) * 45px), calc(var(--py, 0) * 45px), 0)",
        }}
      >
        <img src={FLOAT_2} alt="Floating jacket" className="w-full h-full object-cover" loading="lazy" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 w-full pt-24">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="font-mono-th text-xs sm:text-sm uppercase tracking-[0.35em] text-neutral-400 mb-6"
          data-testid="hero-eyebrow"
        >
          Luxury Streetwear · Curated Thrift
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-display font-black uppercase leading-[0.85] text-6xl sm:text-7xl md:text-8xl lg:text-[9rem] tracking-tight"
          data-testid="hero-headline"
        >
          Wear Your<br />
          <span className="text-neutral-500">Story.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-8 max-w-md text-base md:text-lg text-neutral-300 leading-relaxed"
          data-testid="hero-subheading"
        >
          Curated men's fashion for the ones who don't follow the crowd.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.45 }}
          className="mt-12 flex flex-col sm:flex-row gap-4"
        >
          <Link
            to="/shop?new=1"
            className="group inline-flex items-center justify-center gap-3 bg-white text-black px-8 py-4 font-mono-th text-xs uppercase tracking-[0.2em] font-semibold hover:bg-neutral-200 transition-all duration-300 hover:gap-5"
            data-testid="hero-shop-new-btn"
          >
            Shop New Stock
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/shop"
            className="inline-flex items-center justify-center gap-3 border border-white/25 px-8 py-4 font-mono-th text-xs uppercase tracking-[0.2em] font-semibold hover:bg-white/5 hover:border-white/50 transition-all duration-300"
            data-testid="hero-explore-btn"
          >
            Explore Collection
          </Link>
        </motion.div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <motion.div
          animate={reduce ? {} : { y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="w-5 h-8 rounded-full border border-white/30 flex justify-center pt-2"
        >
          <div className="w-1 h-2 bg-white/60 rounded-full" />
        </motion.div>
      </div>
    </section>
  );
};
